<script setup lang="ts">
  import { useFlowConfigStore } from '@/store/flow-config.store.ts'
  import { FieldConfiguratorTab } from '@/types/enum.ts'
  import FieldConfigRenderer from '@/components/form-wizard/FieldConfigRenderer.vue'
  import { computed, onMounted, ref, watch } from 'vue'
  import PageConfiguration from '@/components/form-wizard/PageConfiguration.vue'
  import FlowConfiguration from '@/components/form-wizard/FlowConfiguration.vue'
  import WizardFormHeading from '@/components/form-wizard/WizardFormHeading.vue'
  import FlowStoreViewer from '@/components/form-wizard/FlowStoreViewer.vue'

  const { currentPage } = defineProps<{
    currentPage: number
  }>()

  const selectedFieldId = defineModel<string | undefined>('selectedFieldId')
  const selectedPageId = defineModel<string | undefined>('selectedPageId')

  type Tab = 'flow' | 'page' | 'data'
  const configTabs: Tab[] = ['flow', 'page', 'data']
  const activeTab = ref<Tab>('flow')

  const store = useFlowConfigStore()
  const tab = ref<FieldConfiguratorTab>(FieldConfiguratorTab.settings)

  const currentFields = computed(() => store.config?.pages[currentPage]?.fields ?? [])
  const selectedField = computed(() =>
    currentFields.value.find(field => field?.id === selectedFieldId.value)
  )

  onMounted(() => {
    if (!store.config) return

    store.config = {
      ...store.config,
      pages: store.config.pages.map(page => ({
        ...page,
        hiddenFields: page.hiddenFields ?? [],
      })),
    }
  })

  const currentPageConfig = computed(() => store.config?.pages[currentPage])

  function deselectField() {
    selectedFieldId.value = undefined
    activeTab.value = 'flow'
  }

  watch(
    () => selectedPageId.value,
    newVal => {
      if (newVal === undefined) {
        activeTab.value = 'flow'
        return
      }
      activeTab.value = 'page'
    }
  )
</script>

<template>
  <section class="config">
    <template v-if="!selectedField">
      <div class="tab-header">
        <button
          v-for="tab in configTabs"
          :key="tab"
          class="tab-btn"
          :class="{ active: activeTab === tab }"
          @click="activeTab = tab"
        >
          {{ tab }}
        </button>
      </div>

      <FlowConfiguration v-if="activeTab === 'flow' && store.config" v-model="store.config" />
      <PageConfiguration
        v-if="activeTab === 'page' && currentPageConfig"
        v-model="currentPageConfig"
      />
      <FlowStoreViewer v-if="activeTab === 'data'" />
    </template>

    <template v-if="selectedField">
      <WizardFormHeading @close="deselectField" title="Field configuration" />

      <div v-if="tab === FieldConfiguratorTab.settings" class="tab-panel" role="tabpanel">
        <FieldConfigRenderer
          :currentPage="currentPage"
          v-model:selectedField="selectedField"
          v-model:selectedFieldId="selectedFieldId"
        />
      </div>
    </template>
  </section>
</template>

<style scoped>
  .config {
    display: flex;
    flex-direction: column;
    padding: 1rem;
    width: 36rem;
    height: 100%;
    max-width: 100%;
    background: transparent;
    background: var(--colours-grey-1);
    overflow: hidden;
  }

  .file-tabs {
    display: flex;
    gap: 0.4rem;
    align-items: flex-end;
    user-select: none;
    margin-bottom: 0.5rem;
    margin-left: -1rem;
    margin-right: -1rem;
  }

  .file-tab {
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0.45rem 1rem;
    background: transparent;
    color: rgb(var(--filetab-text));
    border: none;
    text-decoration: none;
    font-size: 0.85rem;
    cursor: pointer;
    transition: color 120ms ease;
  }

  .file-tab:hover {
    color: rgb(var(--color-black));
  }

  .file-tab.active::after {
    content: '';
    position: absolute;
    left: 0.95rem;
    right: 0.95rem;
    bottom: -0.0625rem;
    height: 0.1875rem;
    background: var(--colours-brand-colour-1);
  }

  .file-tabs-spacer {
    flex: 1;
    border-bottom: 0.0625rem solid transparent;
  }

  .tab-panel {
    padding: 0.5rem 0;
    flex: 1;
    overflow: hidden;
    min-height: 0;
  }

  .config-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
  }

  .close {
    color: rgb(var(--text-secondary));
    cursor: pointer;
    padding: 0.25rem 0;
  }

  .config-title {
    font-size: 1rem;
    font-weight: 700;
    margin: 0;
  }

  .empty {
    display: grid;
    place-items: center;
    height: 100%;
    color: rgb(var(--empty-text));
    font-size: 0.9rem;
  }

  .tab-header {
    display: flex;
    background: var(--colours-grey-2);
  }

  .tab-btn {
    flex: 1;
    padding: 0.75rem 0;
    background: var(--colours-grey-1);
    border: none;
    font-size: 0.8125rem;
    font-weight: 500;
    text-transform: capitalize;
    letter-spacing: 0.04em;
    color: var(--colours-grey-5);
    cursor: pointer;
    position: relative;
    transition: color 0.2s;
    border: 1px solid var(--colours-grey-3);
    box-shadow: 1px 1px 1px 1px var(--colours-grey-3);
  }

  .tab-btn.active {
    transform: translateY(0.125rem);
    box-shadow: none;
  }

  .tab-btn:hover {
    transform: translateY(0.125rem);
    box-shadow: none;
  }

  .tab-btn:first-child {
    border-radius: 0.5rem 0 0 0.5rem;
  }

  .tab-btn:last-child {
    border-radius: 0 0.5rem 0.5rem 0;
  }
</style>
